const citeRegex = /<CITE>(.*?)<\/CITE>/g;

function extractOrderedTokens(summary) {
  if (!summary) return [];
  const tokens = [];
  let match = citeRegex.exec(summary);
  while (match !== null) {
    tokens.push(match[1]);
    match = citeRegex.exec(summary);
  }
  return tokens;
}

// Documents: unchanged — the <CITE> payload equals a flat `referencedCitations[].id` exactly.
export const extractRefsFromSummary = (summary, referencedCitations) => {
  if (!summary || !referencedCitations) return {};

  const summaryRefs = extractOrderedTokens(summary);

  return referencedCitations.reduce((acc, citation, index) => {
    const cnr = citation?.id;
    if (summaryRefs.includes(cnr)) {
      acc[cnr] = index + 1; // 1-based index
    }
    return acc;
  }, {});
};

// Reactions (AR-3.5, resolved): the <CITE> payload is the composite `reactionId-conditionId`
// token (SD-4 item 3) — it is NOT the bare id in `referencedReactions[].id`, so it cannot be
// matched against that array. Number distinct CITATIONS (composite tokens), not distinct
// reactions, in first-seen order, directly off the tokens found in `summary` itself.
export const extractCompositeRefsFromSummary = (summary) => {
  if (!summary) return {};

  const tokens = extractOrderedTokens(summary);
  const refsMap = {};
  let nextIndex = 1;
  tokens.forEach((token) => {
    if (refsMap[token] === undefined) {
      refsMap[token] = nextIndex;
      nextIndex += 1;
    }
  });

  return refsMap;
};

export function replaceRefsInSummaryWithIndexes(summary, referencedCitations, { isReactionSummary = false } = {}) {
  if (!summary) return '';

  const refsFromSummary = isReactionSummary
    ? extractCompositeRefsFromSummary(summary)
    : extractRefsFromSummary(summary, referencedCitations);

  return summary.replace(citeRegex, (match, p1) => {
    const index = refsFromSummary[p1];
    return index ? `(${index})` : match;
  });
}

// FR-4.4/AR-4.2: resolve a composite condition token to the reaction it belongs to by membership
// in that reaction's `referencedConditionIds[]` — never by splitting the token's text on the
// final hyphen, which the BE spec explicitly warns is not a safe identity derivation.
export function findReactionForConditionToken(referencedReactions, token) {
  if (!token || !Array.isArray(referencedReactions)) return null;
  return referencedReactions.find((reaction) => reaction?.referencedConditionIds?.includes(token)) || null;
}

// BE fallback: a reaction citation may contain only the reaction id when no condition row is
// available. Resolve it by exact id membership rather than attempting to parse the token.
export function findReactionForIdToken(referencedReactions, token) {
  if (!token || !Array.isArray(referencedReactions)) return null;
  return referencedReactions.find((reaction) => reaction?.id != null && String(reaction.id) === String(token)) || null;
}
